import React, { useEffect, useState } from 'react';
import { Header } from '../Header';
import { Container } from '../helpers/Container';
import { ProblemDescription } from './ProblemDescription';
import { FileUpload } from './FileUpload';
import { SectionBox } from '../SectionBox';
import { PatientInformation } from './PatientInformation';
import { Button } from '../buttons/Button';
import { Send } from '@material-ui/icons';
import { Title } from '../Title';

import { DoctorInformation } from './DoctorInformation';
import {
  createStyles,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Fade,
  makeStyles,
  Theme
} from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { selectUser } from '../../store/auth/auth.selector';
import { selectRegistration } from '../../store/registration/registration.selector';
import { registrationActions } from '../../store/registration/registration.reducer';
import { ClinicChoice } from './ClinicChoice';
import { getDoctorFormInfo } from '../../store/registration/actions/getDoctorFormInfo';
import { getPatientFormInfo } from '../../store/registration/actions/getPatientFormInfo';
import { Loading } from '../Loading';
import { AnimatedContent } from '../helpers/AnimatedContent';
import { Colors } from '../../constants/colors';
import { AlignContent } from '../helpers/AlignContent';
import { Link } from '../Link';
import { getInvalidFields, getMissingFields, validateRegistration } from '../../store/registration/actions/validateRegistration';
import { Error } from '../Error';
import { Alert } from '@material-ui/lab';
import { getApiErrorMessage } from '../../hooks/getApiErrorMessage';
import { useMountEffect } from '../../hooks/useMountEffect';
import { UserDocument } from '../../model/UserDocument';
import { UploadedFile } from '../../model/UploadedFile';
import { defaultFileCategory } from '../../constants/files';
import { RequestState } from '../../model/enums/RequestState';
import { uploadFile } from '../../store/registration/actions/uploadFile';
import { updateArrayElement } from '../../hooks/updateArrayElement';
import { SecondaryButton } from '../buttons/SecondaryButton';
import { RequestType } from '../../model/enums/RequestType';
import { useNavigate } from 'react-router-dom';
import { RegistrationValidation } from "../../model/RegistrationValidation";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    footer: {
      marginTop: 30
    },
    sendButton: {
      marginTop: 30
    },
    gdprNote: {
      marginTop: 20,
      fontSize: 14,
      color: Colors.dark,
      '& button': {
        fontSize: 14
      },
      [theme.breakpoints.down('xs')]: {
        textAlign: 'center'
      }
    }
  })
);

export const Registration = () => {
  const user = useSelector(selectUser);
  const registrationState = useSelector(selectRegistration);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const classes = useStyles();

  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const [documents, setDocuments] = useState<UserDocument[]>([]);
  const [uploadInProgress, setUploadInProgress] = useState<boolean>(false);
  const [failedDocumentsCount, setFailedDocumentsCount] = useState<number>(0);
  const [uploadsAlertOpen, setUploadsAlertOpen] = useState<boolean>(false);
  const [invalidFields, setInvalidFields] = useState<string[]>([]);

  const [patientEmailValid, setPatientEmailValid] = useState<boolean>(true);
  const [doctorEmailValid, setDoctorEmailValid] = useState<boolean>(true);
  const [patientPhoneValid, setPatientPhoneValid] = useState<boolean>(true);
  const [doctorPhoneValid, setDoctorPhoneValid] = useState<boolean>(true);
  const validationFields: RegistrationValidation = {
      doctor: {
          email: doctorEmailValid,
          phone: doctorPhoneValid
      },
      patient: {
          email: patientEmailValid,
          phone: patientPhoneValid
      }
  };

  useMountEffect(() => {
    if (!user) {
      return;
    }

    /** Init doctor or patient info from logged in user **/
    if (user.requestType === RequestType.DoctorRegistration) {
      dispatch(registrationActions.setDoctor({
        phone: user.phone
      }));
    } else {
      dispatch(registrationActions.setPatient({
        phone: user.phone
      }));
    }

    /** Init form info **/
    const initFormInfo = async (type: RequestType) => {
      setLoading(true);
      try {
        const formInfo = type === RequestType.DoctorRegistration ? await getDoctorFormInfo() : await getPatientFormInfo();
        dispatch(registrationActions.setFormInfo(formInfo));
      } catch (e) {
        setErrorMessage(getApiErrorMessage(e));
      } finally {
        setLoading(false);
      }
    };

    initFormInfo(user.requestType);
  });

  useEffect(() => {
    /** If some document is loading or waiting, set uploadInProgress = true **/
    setUploadInProgress(documents.some(document => document.uploadState === RequestState.LOADING || document.uploadState === RequestState.WAITING));

    /** Update failed documents count **/
    setFailedDocumentsCount(documents.filter(document => document.uploadState === RequestState.ERROR).length);

    /** Update RegistrationState with valid uploads **/
    const validDocuments: UploadedFile[] = documents.reduce((result: UploadedFile[], document) => {
      if (document.uploadedFileId) {
        result.push({
          id: document.uploadedFileId,
          category: document.category
        });
      }
      return result;
    }, []);

    dispatch(registrationActions.setUploads(validDocuments));
  }, [documents, dispatch]);

  const handleRegister = () => {
    setUploadsAlertOpen(false);
    navigate('/send-registration');
  };

  useEffect(() => {
    setInvalidFields(getInvalidFields(validationFields));
  }, [patientEmailValid, doctorEmailValid, patientPhoneValid, doctorPhoneValid]);

  if (!user) {
    return null;
  }

  const handleFileUpload = async (files: File[]) => {
    const uploadedDocuments: UserDocument[] = [];

    for (const file of files) {
      uploadedDocuments.push({
        file: file,
        category: defaultFileCategory,
        uploadState: RequestState.WAITING
      });
    }

    // Add uploaded files to UI
    setDocuments([...documents, ...uploadedDocuments]);

    // Upload files to BE
    for (const document of uploadedDocuments) {
      document.uploadState = RequestState.LOADING;
      try {
        document.uploadedFileId = await uploadFile(document.file);
        document.uploadState = RequestState.SUCCESS;
      } catch (e) {
        document.uploadState = RequestState.ERROR;
        document.errorMessage = getApiErrorMessage(e);
      } finally {
        // Refresh UI
        setDocuments(documents => [...documents]);
      }
    }
  };

  const handleDocumentDelete = (document: UserDocument) => {
    const index = documents.indexOf(document);
    if (index !== -1) {
      documents.splice(index, 1);
    }

    setDocuments([...documents]);
  };

  const handleDocumentCategoryChange = (document: UserDocument, category: string) => {
    document.category = category;
    setDocuments((documents) => updateArrayElement(documents, document));
  };

  const handleRegisterClick = () => {
    // Show uploads alert if some documents are failed
    if (failedDocumentsCount) {
      setUploadsAlertOpen(true);
      return;
    }

    handleRegister();
  };

  const getSuccessfulUploadsLabel = () => {
    const count = registrationState.uploads.length;
    if (!count) {
      return 'bez příloh';
    }

    const withLabel = count > 1 && count < 4 ? 'se' : 's';
    const uploadsLabel = count === 1 ? 'přílohou' : 'přílohami';

    return `${withLabel} ${count} ${uploadsLabel}`;
  };

  const isDepartmentChosen = registrationState.goal && registrationState.clinic;
  const missingFields = getMissingFields(registrationState, user.requestType);
  const successfulUploadsLabel = getSuccessfulUploadsLabel();

  return <>
    <Header showLogout={true} />

    <Container>
      <Title>Žádost o vyšetření</Title>

      <Loading show={loading} />
      <Error errorMessage={errorMessage}
             onClose={() => setErrorMessage('')} />

      <Fade in={!loading && registrationState.formInfo !== undefined}>
        <AnimatedContent>
          <SectionBox order={1}
                      title='Výběr kliniky'
                      description={user.requestType === RequestType.DoctorRegistration ? 'Vyberte, prosím, za jakým cílem a na jakou kliniku IKEM si přejete poslat svého pacienta.' : 'Vyberte, prosím, na jakou kliniku IKEM se chcete obrátit.'}
                      disabled={false}>
            <ClinicChoice />
          </SectionBox>

          <SectionBox order={2}
                      title='Popis problému'
                      description={user.requestType === RequestType.DoctorRegistration ? 'Uveďte důvod předání pacienta, případně Vaši pracovní diagnózu.' : 'Popište s jakým problémem se na nás obracíte.'}
                      disabled={!isDepartmentChosen}>
            <ProblemDescription />
          </SectionBox>

          <SectionBox order={3}
                      title='Nahrání příloh'
                      description='Nahrajte doprovodné dokumenty.'
                      disabled={!isDepartmentChosen}>
            <FileUpload onFileUpload={handleFileUpload}
                        onDocumentDelete={handleDocumentDelete}
                        onCategoryChange={handleDocumentCategoryChange}
                        documents={documents} />
          </SectionBox>

          <SectionBox order={4}
                      title='Osobní údaje pacienta'
                      description={user.requestType === RequestType.DoctorRegistration ? 'Zanechte nám osobní údaje pacienta pro snazší komunikaci a urychlení administrativy.' : 'Zanechte nám své osobní údaje pro snazší komunikaci a urychlení administrativy.'}
                      disabled={!isDepartmentChosen}>
            <PatientInformation onChangePhoneValid={setPatientPhoneValid} emailValidonChangeEmailValid={setPatientEmailValid} />
          </SectionBox>

          <SectionBox order={5}
                      title='Kontakt na lékaře'
                      description={user.requestType === RequestType.DoctorRegistration ? 'Zanechte nám kontaktní informace na ošetřujícího lékaře, na kterého se můžeme obrátit.' : 'Zanechte nám kontaktní informace na Vašeho ošetřujícího lékaře, na kterého se můžeme obrátit.'}
                      disabled={!isDepartmentChosen}>
            <DoctorInformation onPhoneValid={setDoctorPhoneValid} onEmailValid={setDoctorEmailValid} />
          </SectionBox>

          <AlignContent to='center' className={classes.footer}>
            {(() => {
              if (missingFields.length) {
                return <Alert severity='warning'>
                  K odeslání žádosti prosím vyplňte: <strong>{missingFields.join(', ')}</strong>.
                </Alert>;
              }

              if (invalidFields.length) {
                return <Alert severity='warning'>
                  Nasledující informace nejsou validní: <strong>{invalidFields.join(', ')}</strong>.
                </Alert>;
              }

              if (uploadInProgress) {
                return <Alert severity='warning'>Vyčkejte, prosím, na dokončení nahrání souborů.</Alert>;
              }
            })()}

            <Button onClick={handleRegisterClick}
                    className={classes.sendButton}
                    disabled={!user || uploadInProgress || !validateRegistration(registrationState, user.requestType, validationFields) || !!invalidFields.length}>
              Odeslat <Send />
            </Button>

            <p className={classes.gdprNote}>
              Odeslaním formuláře souhlasíte se <Link
              onClick={() => window.open('https://www.ikem.cz/cs/o-nas/gdpr/informace-pro-pacienty-ikem-o-zpracovani-osobnich-udaju/a-3262/', '_blank')}>
              zpracováním osobních údajů</Link>.
            </p>
          </AlignContent>
        </AnimatedContent>
      </Fade>

      <Dialog open={uploadsAlertOpen}
              onClose={() => setUploadsAlertOpen(false)}
              aria-labelledby='alert-dialog-title'
              aria-describedby='alert-dialog-description'>
        <DialogTitle>
          Nahrání {failedDocumentsCount} {failedDocumentsCount === 1 ? 'souboru' : 'souborů'} selhalo. Přejete si i přesto požadavek
          odeslat?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Požadavek se odešle {successfulUploadsLabel}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <SecondaryButton onClick={() => setUploadsAlertOpen(false)}>
            Zrušit
          </SecondaryButton>
          <Button onClick={handleRegisterClick} autoFocus>
            Odeslat {successfulUploadsLabel}
            <Send />
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  </>;
};
